import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, ModalBody, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowExchangeIcon } from 'assets/icon-arrow-exchange.svg';

import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import CustomSelect from 'shared/components/CustomSelect';
import MainSpinner from 'shared/components/MainSpinner';

import useApi from 'hooks/useApi';
import useLanguages from 'hooks/useLanguages';

import { showAlert } from 'redux/actions/alertActions';

import { CustomFolderProps } from 'shared/prop-types/ReducerProps';
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, emptyString } from 'shared/utils';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { colors } from 'shared/defaultTheme';

import { CustomModalWrapper, TranslateLabel } from './styles';

const TranslateFormModal = ({ folders, onCloseModal, folderRoot, loadCustomFolders }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cusNavId, mail } = useSelector((state) => state.auth);
  const { mainColor } = useSelector((state) => state.theme);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { customAreaApi } = useApi();
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [translateFolders, setTranslateFolders] = useState([]);
  const { languages } = useLanguages();

  const [{ loading: loadingPost }, postFolder] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  useEffect(() => {
    setTranslateFolders(folders.filter((x) => x.visible && x.pathId !== '1/'));
  }, [folders]);

  const onChangeLanguage = (e) => {
    const { target } = e;
    const { value } = target;
    setSelectedLanguage(value);
  };

  const findName = (currentFolder, codeLang, dataDefault) => {
    const findI18n = currentFolder.i18List.find((i18object) => i18object.i18Code === codeLang);
    if (findI18n) {
      return findI18n.i18Name;
    }
    if (dataDefault && !findI18n) {
      return currentFolder.i18List[0].i18Name;
    }
    return '';
  };

  const onChangeValue = (event, folder) => {
    const { target } = event;
    const { value } = target ?? { value: '' };
    let newFolder = {};
    if (value && value.length > 0) {
      const i18Item = folder.i18List.find((i18object) => i18object.i18Code === selectedLanguage);
      if (i18Item) {
        newFolder = {
          ...folder,
          pathId: folder.pathId,
          permissions: [
            {
              u: mail,
              p: 'rw-',
            },
          ],
          visible: true,
          i18List: [
            ...folder.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      } else {
        newFolder = {
          ...folder,
          pathId: folder.pathId,
          permissions: [
            {
              u: mail,
              p: 'rw-',
            },
          ],
          visible: true,
          i18List: [
            ...folder.i18List,
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      }
    } else {
      newFolder = {
        ...folder,
        pathId: folder.pathId,
        permissions: [
          {
            u: mail,
            p: 'rw-',
          },
        ],
        visible: true,
        i18List: [
          ...folder.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
          {
            i18Code: selectedLanguage,
            i18name: '',
          },
        ],
      };
    }
    setTranslateFolders(
      translateFolders.map((currentFolder) => {
        if (currentFolder.id === newFolder.id) {
          return newFolder;
        }
        return currentFolder;
      })
    );
  };

  const translate = async () => {
    try {
      const {
        data: { errors },
      } = await postFolder({
        url: `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}/modify`,
        data: translateFolders,
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }

      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
      loadCustomFolders();
    } catch (ex) {
      console.error('Error on translate folders: ', ex);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
    }
  };

  const Title = () => (
    <Row className="row-title mb-4">
      <Col sm={12}>
        <h3 className="text-uppercase">{t('custom_folder_translate')}</h3>
      </Col>
    </Row>
  );
  return (
    <CustomModalWrapper className="translate-modal" isOpen toggle={onCloseModal} size="lg">
      <ModalHeader
        className="modal-header"
        backgroundColor={colors.transparent}
        onClose={onCloseModal}
      />
      <ModalBody>
        <Title />
        <Row className="remove-margin-horizontal mb-2">
          <Col className="d-flex flex-row justify-content-end" sm={12}>
            <CustomSelect
              defaultValue={
                emptyString(selectedLanguage) ? selectedDefaultLanguage : selectedLanguage
              }
              options={languages}
              onChange={onChangeLanguage}
            />
          </Col>
        </Row>
        <form className="form" noValidate>
          <Row className="remove-margin-horizontal mb-2">
            <Col sm={6}>
              <TranslateLabel color={mainColor}>{t('custom_folder_folder_name')}</TranslateLabel>
            </Col>
            <Col sm={1} />
            <Col sm={5}>
              <TranslateLabel color={mainColor}>{t('custom_folder_translation')}</TranslateLabel>
            </Col>
          </Row>
          {translateFolders.length > 0 &&
            translateFolders
              .filter((folder) => {
                const pathIds = folder.pathId.split('/');
                const clearedPathIds = pathIds.filter((x) => x.length > 0);
                const parentId = clearedPathIds[clearedPathIds.length - 2];
                return parentId === folderRoot.id;
              })
              .map((folder) => (
                <Row key={folder.id} className="remove-margin-horizontal">
                  <Col sm={6}>
                    <div className="form__form-group">
                      <TextField value={findName(folder, selectedDefaultLanguage, true)} />
                    </div>
                  </Col>
                  <Col sm={1} className="d-flex align-items-center justify-content-center pb-3">
                    <ArrowExchangeIcon className="translate-icon" fill={mainColor} />
                  </Col>
                  <Col sm={5}>
                    <div className="form__form-group">
                      <TextField
                        value={findName(folder, selectedLanguage || selectedDefaultLanguage, false)}
                        onChange={(event) => {
                          if (
                            !emptyString(selectedLanguage) &&
                            selectedLanguage !== selectedDefaultLanguage
                          ) {
                            onChangeValue(event, folder);
                          }
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              ))}
          <Row className="remove-margin-horizontal mb-2">
            <Col sm={6}>
              <TranslateLabel color={mainColor}>{t('custom_folder_subfolder_name')}</TranslateLabel>
            </Col>
            <Col sm={1} />
            <Col sm={5}>
              <TranslateLabel color={mainColor}>{t('custom_folder_translation')}</TranslateLabel>
            </Col>
          </Row>
          {translateFolders.length > 0 &&
            translateFolders
              .filter((folder) => {
                const pathIds = folder.pathId.split('/');
                const clearedPathIds = pathIds.filter((x) => x.length > 0);
                const parentId = clearedPathIds[clearedPathIds.length - 2];
                return parentId === folderRoot.id;
              })
              .map((folder) => (
                <>
                  {translateFolders
                    .filter((currentFolder) => {
                      const pathIds = currentFolder.pathId.split('/');
                      const clearedPathIds = pathIds.filter((x) => x.length > 0);
                      const parentId = clearedPathIds[clearedPathIds.length - 2];
                      return parentId === folder.id;
                    })
                    .map((currentFolder) => (
                      <Row key={currentFolder.id} className="remove-margin-horizontal">
                        <Col sm={6}>
                          <div className="form__form-group">
                            <TextField
                              value={findName(currentFolder, selectedDefaultLanguage, true)}
                            />
                          </div>
                        </Col>
                        <Col
                          sm={1}
                          className="d-flex align-items-center justify-content-center pb-3"
                        >
                          <ArrowExchangeIcon className="translate-icon" fill={mainColor} />
                        </Col>
                        <Col sm={5}>
                          <div className="form__form-group">
                            <TextField
                              value={findName(
                                currentFolder,
                                selectedLanguage || selectedDefaultLanguage,
                                false
                              )}
                              onChange={(event) => {
                                if (
                                  !emptyString(selectedLanguage) &&
                                  selectedLanguage !== selectedDefaultLanguage
                                ) {
                                  onChangeValue(event, currentFolder);
                                }
                              }}
                            />
                          </div>
                        </Col>
                      </Row>
                    ))}
                </>
              ))}

          <Row className="w-100 remove-margin-horizontal">
            <Col className="d-flex flex-row justify-content-end" sm={12}>
              <Button
                className="text-uppercase mr-4"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCloseModal}
              />
              <Button
                className="text-uppercase"
                type="button"
                color="primary"
                label={t('general_button_save')}
                onClick={translate}
              />
            </Col>
          </Row>
        </form>
        {loadingPost && <MainSpinner />}
      </ModalBody>
    </CustomModalWrapper>
  );
};

TranslateFormModal.propTypes = {
  folders: PropTypes.arrayOf(CustomFolderProps).isRequired,
  onCloseModal: PropTypes.func.isRequired,
  folderRoot: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  loadCustomFolders: PropTypes.func.isRequired,
};

export default TranslateFormModal;
